import create from 'zustand/vanilla';

import { fetchGlorillsProfiles, type GlorillsProfile } from '@/services/glorills.service';
import { BASE_LOCALE_CODE, sortByLocaleOrder } from '@/constant/profileLinkLocales';

/**
 * 프로필링크 다국어 상태 — 글로릴스에 연동된 인스타 계정(언어)별 프로필과
 * 지금 편집·미리보기 중인 언어를 들고 있다.
 *
 * 원본(ko)은 블록의 기존 필드가 그대로 내용이 되고, 다른 언어는 block_content.i18n 에서 꺼낸다.
 * 자세한 규칙은 src/utils/blockI18n.ts 참고.
 */
interface ProfileLinkLocaleState {
    /** 이 목록의 주인 — 계정 전환을 감지해 이전 사용자의 목록이 새 계정에 보이지 않게 한다 */
    ownerUserId: string | null;
    /** 연동된 계정 목록 (PROFILE_LINK_LOCALES 순서로 정렬됨) */
    profiles: GlorillsProfile[];
    /** 지금 보고 있는 언어 코드. 원본은 'ko' */
    activeLocale: string;
    loading: boolean;
    loaded: boolean;
    error: string | null;
}

export interface ProfileLinkLocaleStore extends ProfileLinkLocaleState {
    setActiveLocale: (lang: string) => void;
    loadProfiles: (userId: string) => Promise<void>;
    reset: () => void;
}

const initializeState: ProfileLinkLocaleState = {
    ownerUserId: null,
    profiles: [],
    activeLocale: BASE_LOCALE_CODE,
    loading: false,
    loaded: false,
    error: null,
};

export const profileLinkLocaleStore = create<ProfileLinkLocaleStore>()((set, get) => ({
    ...initializeState,

    setActiveLocale: (lang: string) => set({ activeLocale: lang }),

    loadProfiles: async (userId: string) => {
        const { loading, loaded, ownerUserId } = get();
        if (loading) return;
        // 같은 사용자면 캐시 유지, 다른 사용자면(로그아웃→다른 계정 로그인) 전부 버리고 다시
        if (loaded && ownerUserId === userId) return;
        set({ ...initializeState, ownerUserId: userId, loading: true });
        try {
            const list = await fetchGlorillsProfiles(userId);
            // null = 미확정(토큰 미도착 등) — loaded 로 굳히지 않고 다음 트리거(토큰 도착)에 재시도.
            // 그대로 두면 SSO 리다이렉트 복귀 직후 빈 목록이 캐시돼 새로고침 전엔 칩이 안 뜬다.
            if (list === null) {
                set({ loading: false });
                return;
            }
            // 원본(ko) 칩은 연동 여부와 무관하게 항상 앞에 둔다 — 공개 페이지(spacehome)와 동일 규약.
            // 연동 언어가 1개여도 [ko, 그언어]=2개가 되어 전환 칩이 뜬다(편집기에서 base 누락 버그 방지).
            const withBase =
                list.some((p) => p.lang === BASE_LOCALE_CODE) || list.length === 0
                    ? list
                    : [{ lang: BASE_LOCALE_CODE, username: '', name: '', avatarUrl: null, bio: null }, ...list];
            const sorted = sortByLocaleOrder(withBase, (p) => p.lang);
            set((state) => ({
                profiles: sorted,
                loaded: true,
                // 지금 보던 언어가 목록에서 사라졌으면(연동 해제) 원본으로 되돌린다
                activeLocale: sorted.some((p) => p.lang === state.activeLocale)
                    ? state.activeLocale
                    : BASE_LOCALE_CODE,
            }));
        } catch (e) {
            set({ error: e instanceof Error ? e.message : '연동 계정을 불러오지 못했습니다' });
        } finally {
            set({ loading: false });
        }
    },

    reset: () => set(initializeState),
}));

export default profileLinkLocaleStore;
